// Types for KAF International Group Admin Dashboard

export enum UserRole {
  SUPER_ADMIN = 'super_admin',
  DASHBOARD_ADMIN = 'dashboard_admin',
}

export interface User {
  id: number;
  name?: string;
  email: string;
  role: UserRole;
  createdAt: string;
  updatedAt: string;
}

export interface UserPayload {
  id: number;
  name?: string;
  email: string;
  role: UserRole;
}

export interface AuthResponse {
  token: string;
  user: UserPayload;
}

export interface Announcement {
  id: number;
  contentAr: string;
  contentEn?: string;
  isActive: boolean;
  orderIndex: number;
  createdAt: string;
  updatedAt: string;
}

export interface TeamMember {
  id: number;
  nameAr: string;
  nameEn: string;
  positionAr: string;
  positionEn: string;
  bioAr?: string;
  bioEn?: string;
  image?: string;
  isFounder: boolean;
  isPinned: boolean;
  orderIndex: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface BlogPostMedia {
  id: number;
  imageUrl: string;
  mediaType: 'image' | 'video';
  orderIndex: number;
}

export interface BlogPost {
  id: number;
  titleAr: string;
  titleEn: string;
  slugAr: string;
  slugEn: string;
  excerptAr?: string;
  excerptEn?: string;
  contentAr: string;
  contentEn: string;
  image?: string;
  media?: BlogPostMedia[];
  readingTime: number;
  isPublished: boolean;
  publishedAt?: string;
  authorId?: number;
  createdAt: string;
  updatedAt: string;
}

export enum ConsultationType {
  PHONE = 'phone',
  OFFICE = 'office',
  ONLINE = 'online',
}

export enum ConsultationStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export interface Consultation {
  id: number;
  name: string;
  email: string;
  phone: string;
  consultationType: ConsultationType;
  duration: number;
  message?: string;
  status: ConsultationStatus;
  assignedTo?: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export type ArticleDraftStatus = 'draft' | 'published';
export type ArticleDraftLanguage = 'ar' | 'en' | 'mixed' | 'unknown';
export type ArticleDraftEditType = 'none' | 'manual' | 'ai' | 'translation';
export type ArticleDraftPublishedVersion = 'original' | 'manual' | 'ai' | 'translation';

export interface ArticleDraftImage {
  id: number;
  articleDraftId: number;
  imageUrl: string;
  orderIndex: number;
}

export interface ArticleDraft {
  id: number;
  parentDraftId?: number | null;
  rootDraftId?: number;
  childDraftCount?: number;
  language: ArticleDraftLanguage;
  isBilingual?: boolean;
  title: string;
  editedTitle?: string | null;
  originalContent: string;
  manualContent?: string | null;
  aiContent?: string | null;
  translationContent?: string | null;
  editType: ArticleDraftEditType;
  originalUrl: string;
  sourceName: string;
  imageUrl?: string | null;
  images?: ArticleDraftImage[];
  sourcePublishedAt?: string | null;
  status: ArticleDraftStatus;
  publishedVersion?: ArticleDraftPublishedVersion | null;
  blogPostId?: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface ArticleDraftListItem {
  id: number;
  parentDraftId?: number | null;
  childDraftCount?: number;
  language: ArticleDraftLanguage;
  isBilingual: boolean;
  title: string;
  editedTitle?: string | null;
  sourceName: string;
  originalUrl: string;
  imageUrl?: string | null;
  sourcePublishedAt?: string | null;
  status: ArticleDraftStatus;
  editType: ArticleDraftEditType;
  publishedVersion?: ArticleDraftPublishedVersion | null;
  blogPostId?: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface Location {
  id: number;
  image?: string;
  titleAr: string;
  titleEn: string;
  locationAr: string;
  locationEn: string;
  orderIndex: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

